/**
 * Top-level channel type. The only axis that determines who CAN participate,
 * not who actually does — that's controlled by chat_participant rows.
 *   - dm:               two internal users, 1:1
 *   - group:            Slack-style internal channel, ≥2 internal users
 *   - customer_support: one portal_user + ≥1 internal rep; optional system/AI
 */
export enum ChatChannelType {
	Dm = 'dm',
	Group = 'group',
	CustomerSupport = 'customer_support',
}

export enum ChatConversationStatus {
	Active = 'active',
	Archived = 'archived',
	Resolved = 'resolved',
}

/**
 * Who can see and join this conversation without an explicit invite.
 *   - private: explicit participants only (DMs are always private)
 *   - public:  any internal user in the tenant can see + post;
 *              posting auto-creates a participant row
 *   - team:    members of any linked team (chat_conversation_team)
 *              can see + post; auto-join-on-post
 *   - role:    holders of any linked role (chat_conversation_role)
 *              can see + post; auto-join-on-post
 *
 * Explicit membership (chat_participant.isActive) is still respected
 * alongside the visibility rule — adding someone manually to a public
 * or team/role group forces their row to exist so it surfaces in the
 * "My groups" section right away rather than only in "Discoverable".
 */
export enum ChatConversationVisibility {
	Private = 'private',
	Public = 'public',
	Team = 'team',
	Role = 'role',
}

/**
 * Discriminates WHO a participant is. Enforced at MembershipService:
 *   dm:               internal_user only, exactly 2
 *   group:            internal_user only, ≥2
 *   customer_support: exactly 1 portal_user + ≥1 internal_user;
 *                     system and external_* are optional bridges
 */
export enum ChatParticipantType {
	InternalUser = 'internal_user',
	PortalUser = 'portal_user',
	ExternalSms = 'external_sms',
	ExternalEmail = 'external_email',
	System = 'system',
}

export enum ChatParticipantRole {
	Owner = 'owner',
	Admin = 'admin',
	Member = 'member',
	/** For customer_support portal users — implicit in the role type itself. */
	Guest = 'guest',
}

export enum ChatMessageKind {
	Text = 'text',
	File = 'file',
	/** Renders as centered pill in UI: "Alice joined", "Conversation resolved", etc. */
	System = 'system',
	/** Future: call-join/leave events for voice integration. */
	CallEvent = 'call_event',
}

/**
 * Queue-entry lifecycle for customer_support conversations. Distinct from
 * conversation.status because a conversation can be `active` while its
 * queue entry goes through waiting → assigned → resolved. Only
 * customer_support conversations ever have a queue entry.
 */
export enum ChatQueueEntryStatus {
	/** Not yet claimed by a rep. Portal user sees "connecting you…". */
	Waiting = 'waiting',
	/** A rep has claimed/been-assigned this chat. */
	Assigned = 'assigned',
	/** Portal user closed or timed out before a rep picked up. */
	Abandoned = 'abandoned',
	/** Rep resolved the chat. Portal user can no longer resume. */
	Resolved = 'resolved',
}

/**
 * How the queue entry expects to be routed. Drives which rep the
 * RoutingService tries first.
 */
export enum ChatQueueRoutingMode {
	/** Portal user has an assigned rep; try them first, fall back to pool. */
	AssignedRepPreferred = 'assigned_rep_preferred',
	/** No rep preference — go straight to the pool. */
	PoolOnly = 'pool_only',
	/** Rep explicitly requested by the portal user (rare; future use). */
	SpecificRep = 'specific_rep',
}
